import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ZoomIn, RotateCw, Check, X, Crop } from "lucide-react";

// Crop alanını canvas'a uygula
const createCroppedImage = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = new Image();
  image.src = imageSrc;
  
  await new Promise((resolve) => {
    image.onload = resolve;
  });
  
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));
  
  canvas.width = safeArea;
  canvas.height = safeArea;
  
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);
  
  ctx.drawImage(
    image,
    safeArea / 2 - image.width / 2,
    safeArea / 2 - image.height / 2
  );
  
  const data = ctx.getImageData(0, 0, safeArea, safeArea);
  
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  
  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width / 2 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height / 2 - pixelCrop.y)
  );
  
  return canvas.toDataURL('image/jpeg', 0.9);
};

const ImageCropper = ({ open, onClose, imageSrc, onCropComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1); // Default kare
  
  const aspectRatios = [
    { label: "1:1 (Kare)", value: 1 },
    { label: "4:5 (Instagram)", value: 4/5 },
    { label: "9:16 (Story)", value: 9/16 },
    { label: "16:9 (Yatay)", value: 16/9 },
    { label: "4:3 (Klasik)", value: 4/3 },
    { label: "Serbest", value: undefined },
  ];

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropAreaChange = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleApplyCrop = async () => {
    if (!croppedAreaPixels) return;
    
    setProcessing(true);
    try {
      const croppedImage = await createCroppedImage(imageSrc, croppedAreaPixels, rotation);
      onCropComplete(croppedImage);
      onClose();
    } catch (error) {
      console.error("Görsel işleme hatası:", error);
      onCropComplete(imageSrc);
      onClose();
    } finally {
      setProcessing(false);
    }
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const resetCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crop className="h-5 w-5 text-emerald-600" />
            Görseli Düzenle
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Image Cropper Area */}
          <div className="relative h-[300px] bg-gray-100 rounded-lg overflow-hidden">
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspectRatio}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropComplete={onCropAreaChange}
                objectFit="contain"
              />
            )}
          </div>
          
          {/* Aspect Ratio Selection */}
          <div className="flex items-center gap-3">
            <Label className="text-sm font-medium whitespace-nowrap">Oran:</Label>
            <Select 
              value={aspectRatio?.toString() || "free"} 
              onValueChange={(val) => setAspectRatio(val === "free" ? undefined : parseFloat(val))}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Oran seç" />
              </SelectTrigger>
              <SelectContent>
                {aspectRatios.map((ratio) => (
                  <SelectItem 
                    key={ratio.label} 
                    value={ratio.value?.toString() || "free"}
                  >
                    {ratio.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Zoom Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium flex items-center gap-2">
                <ZoomIn className="h-4 w-4" />
                Yakınlaştır
              </Label>
              <span className="text-sm text-gray-500">{Math.round(zoom * 100)}%</span>
            </div>
            <Slider
              value={[zoom]}
              onValueChange={([val]) => setZoom(val)}
              min={1}
              max={3}
              step={0.1}
              className="w-full"
            />
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRotate}
              className="flex-1"
            >
              <RotateCw className="h-4 w-4 mr-2" />
              Döndür
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={resetCrop}
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              Sıfırla
            </Button>
          </div>
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={processing}>
            İptal
          </Button>
          <Button 
            onClick={handleApplyCrop} 
            disabled={processing}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                İşleniyor...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Uygula
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropper;
